import React, { useEffect, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Star, Quote, Lock, Send, Check, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { guestbookApi } from '@/lib/api';

const LivreDorPage = () => {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    message: '',
    rating: 5,
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);

  const loadEntries = async () => {
    try {
      setIsLoading(true);
      const data = await guestbookApi.list();
      setEntries(data);
    } catch (err) {
      toast.error('Impossible de charger les témoignages.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    setPasswordError(false);
    try {
      await guestbookApi.verifyPassword(password);
      setIsAuthenticated(true);
      setIsAuthDialogOpen(false);
      toast.success('Accès accordé ! Vous pouvez maintenant laisser un avis.');
    } catch (err) {
      setPasswordError(true);
      toast.error('Mot de passe incorrect.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.message.trim()) {
      toast.error('Veuillez remplir tous les champs.');
      return;
    }
    setIsSubmitting(true);
    try {
      const newEntry = await guestbookApi.create({
        password,
        name: formData.name.trim(),
        message: formData.message.trim(),
        rating: formData.rating,
      });
      setEntries([newEntry, ...entries]);
      setFormData({ name: '', message: '', rating: 5 });
      setIsDialogOpen(false);
      toast.success('Merci pour votre témoignage !');
    } catch (err) {
      const detail = err?.response?.data?.detail || "Erreur lors de l'envoi.";
      toast.error(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onSelect = null) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        data-testid={interactive ? `rating-star-${i + 1}` : undefined}
        className={`w-5 h-5 cursor-pointer transition-colors ${
          i < rating
            ? 'fill-[var(--color-gold)] text-[var(--color-gold)]'
            : 'text-[var(--color-beige)] hover:text-[var(--color-gold)]'
        }`}
        onClick={() => interactive && onSelect && onSelect(i + 1)}
      />
    ));
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[var(--surface-cream)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block text-sm uppercase tracking-wider text-[var(--color-gold)] font-medium mb-4">
              Témoignages
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--text-primary)] mb-6">
              Livre d'Or
            </h1>
            <div className="w-24 h-px bg-[var(--color-gold)] mx-auto mb-8"></div>
            <p className="text-lg text-[var(--text-secondary)] leading-relaxed">
              Découvrez les témoignages de nos familles adoptantes et partagez à votre tour votre expérience.
            </p>
          </div>
        </div>
      </section>

      {/* Add Review Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            {!isAuthenticated ? (
              <Dialog open={isAuthDialogOpen} onOpenChange={setIsAuthDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="open-auth-dialog-btn" className="btn-primary">
                    <Lock className="w-4 h-4 mr-2" />
                    Laisser un avis
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Accès protégé</DialogTitle>
                    <DialogDescription>
                      Entrez le mot de passe pour laisser un avis. Ce mot de passe vous a été communiqué lors de l'adoption.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handlePasswordSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">Mot de passe</Label>
                      <Input
                        id="password"
                        data-testid="guestbook-password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`form-input ${passwordError ? 'border-red-500' : ''}`}
                        placeholder="Entrez le mot de passe"
                      />
                      {passwordError && (
                        <p className="text-sm text-red-500 flex items-center gap-1">
                          <X className="w-4 h-4" />
                          Mot de passe incorrect
                        </p>
                      )}
                    </div>
                    <Button
                      type="submit"
                      data-testid="guestbook-password-submit-btn"
                      className="btn-primary w-full"
                      disabled={isVerifying}
                    >
                      {isVerifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Valider
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            ) : (
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="open-review-dialog-btn" className="btn-primary">
                    <Check className="w-4 h-4 mr-2" />
                    Laisser votre avis
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Votre témoignage</DialogTitle>
                    <DialogDescription>
                      Partagez votre expérience avec nous.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Votre nom</Label>
                      <Input
                        id="name"
                        data-testid="guestbook-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="form-input"
                        placeholder="Marie D."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Votre note</Label>
                      <div className="flex gap-1" data-testid="guestbook-rating-stars">
                        {renderStars(formData.rating, true, (rating) =>
                          setFormData({ ...formData, rating })
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Votre message</Label>
                      <Textarea
                        id="message"
                        data-testid="guestbook-message-input"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="form-input min-h-[120px]"
                        placeholder="Partagez votre expérience..."
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="guestbook-submit-btn"
                      className="btn-primary w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Publier mon avis
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-24 bg-[var(--surface-cream)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="guestbook-entries-grid">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--color-gold)]" />
            </div>
          ) : entries.length === 0 ? (
            <p className="text-center text-[var(--text-secondary)] py-12">
              Aucun témoignage pour le moment. Soyez le premier à partager votre expérience !
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  data-testid={`guestbook-entry-${entry.id}`}
                  className="testimonial-card hover-lift"
                >
                  <Quote className="w-10 h-10 text-[var(--color-gold)] opacity-30 mb-4" />
                  <p className="text-[var(--text-secondary)] leading-relaxed mb-6">
                    "{entry.message}"
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[var(--text-primary)]">{entry.name}</p>
                      <p className="text-sm text-[var(--text-subtle)]">
                        {new Date(entry.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                        })}
                      </p>
                    </div>
                    <div className="flex gap-0.5">{renderStars(entry.rating)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default LivreDorPage;
